import { useEffect, useMemo, useState } from 'react';
// (useMemo is used by the template picker's active-template filter.)
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckSquare, Square, Plus, ListChecks, Link2, LayoutTemplate, ChevronDown, ChevronRight } from 'lucide-react';
import { tasksApi } from '../../api/tasks';
import { taskTemplatesApi } from '../../api/taskTemplates';
import { companiesApi } from '../../api/companies';
import Modal from '../Modal';
import { StatusBadge } from '../badges';
import TaskDetailModal from './TaskDetailModal';
import TaskAssigneeSelect from './TaskAssigneeSelect';
import { canManageTask, summaryText, summarizeTasks, type TaskAccess } from '../../lib/tasks';
import type { TicketTask, TaskSummary, TaskTemplate } from '../../types';

/* Sub-ticket tasks on a ticket (cTKT-00211): the checklist card. Rows show
   who each task is waiting on, a linked full ticket when there is one, and
   completion. Completing always goes through a note (required); reopening
   is one click. Staff and collaborators add tasks (typed, or from a
   template); staff / collaborators / the task's assignee work them. */

export interface TaskListProps {
  ticketId: string;
  companyId: string | null | undefined;
  tasks: TicketTask[];
  summary?: TaskSummary | null;
  myId?: string | null;
  isStaff: boolean;
  isCollaborator: boolean;
  /** Where ticket links go: the staff app ('/tickets') or the portal. */
  linkBase?: string;
  /** The ticket sends update text in emails; task notes then offer the per-update opt-out. */
  emailIncludeContent?: boolean;
  /** Extra invalidation after any change (the page's own queries). */
  onChanged?: () => void;
}

const fmtShort = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

// ─── Complete-with-note dialog ────────────────────────────────

function CompleteTaskModal({
  ticketId, task, emailIncludeContent, onClose, onDone,
}: { ticketId: string; task: TicketTask; emailIncludeContent?: boolean; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [include, setInclude] = useState(true);
  const mutation = useMutation({
    mutationFn: () => tasksApi.complete(ticketId, task.id, note.trim(), emailIncludeContent ? include : undefined),
    onSuccess: () => { toast.success(`Completed "${task.title}"`); onDone(); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message ?? 'Could not complete the task'),
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={mutation.isPending}
      title={`Complete: ${task.title}`}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!note.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Completing…' : 'Mark complete'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted">
          A note is required to complete a task — say what was done or what came of it. It is posted to the
          ticket's conversation and the people following the ticket are told.
        </p>
        <textarea
          autoFocus
          className="input resize-y text-sm"
          rows={4}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="What was done?"
          aria-label="Completion note"
        />
        {emailIncludeContent && (
          <label className="flex items-start gap-2 cursor-pointer text-xs">
            <input type="checkbox" className="w-3.5 h-3.5 mt-0.5 accent-[var(--scpa-primary)]" checked={include} onChange={e => setInclude(e.target.checked)} />
            <span>Include this note's text in notification emails</span>
          </label>
        )}
      </div>
    </Modal>
  );
}

// ─── Add-from-template dialog ─────────────────────────────────

function TemplatePickerModal({ ticketId, onClose, onAdded }: { ticketId: string; onClose: () => void; onAdded: () => void }) {
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['task-templates'],
    queryFn: () => taskTemplatesApi.list(),
  });
  const active = useMemo(() => (templates as TaskTemplate[]).filter(t => t.isActive && t.items.length > 0), [templates]);
  const [templateId, setTemplateId] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const selected = active.find(t => t.id === templateId) ?? null;

  // Picking a template pre-checks every item — the common case is "all of it".
  useEffect(() => {
    setChecked(new Set(selected?.items.map(i => i.id) ?? []));
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutation = useMutation({
    mutationFn: () => tasksApi.addFromTemplate(ticketId, templateId, [...checked]),
    onSuccess: (tasks) => { toast.success(`Added ${tasks.length} task${tasks.length === 1 ? '' : 's'}`); onAdded(); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message ?? 'Could not add tasks'),
  });

  const toggle = (id: string) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Modal
      open
      onClose={onClose}
      busy={mutation.isPending}
      title="Add tasks from a template"
      width={560}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selected || checked.size === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Adding…' : `Add ${checked.size > 0 ? `${checked.size} ` : ''}task${checked.size === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      {isLoading ? (
        <p className="text-sm text-muted">Loading templates…</p>
      ) : active.length === 0 ? (
        <p className="text-sm text-muted">
          No task templates yet. An admin can create them in Company Settings → Task templates.
        </p>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="label" htmlFor="task-template-select">Template</label>
            <select id="task-template-select" className="select" value={templateId} onChange={e => setTemplateId(e.target.value)}>
              <option value="">Choose a template…</option>
              {active.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.items.length})</option>
              ))}
            </select>
          </div>
          {selected && (
            <div className="space-y-1">
              {selected.description && <p className="text-xs text-muted">{selected.description}</p>}
              <p className="text-xs font-semibold uppercase tracking-wider text-muted">Tasks to add</p>
              {selected.items.map(item => (
                <label key={item.id} className="flex items-start gap-2.5 px-2 py-1.5 rounded-lg hover:bg-row-odd cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 mt-0.5 accent-[var(--scpa-primary)] shrink-0"
                    checked={checked.has(item.id)}
                    onChange={() => toggle(item.id)}
                  />
                  <span className="min-w-0">
                    <span className="font-medium text-fg">{item.title}</span>
                    {item.description && <span className="block text-xs text-muted">{item.description}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── One task row ─────────────────────────────────────────────
// A real component (not a closure inside the card): an inline component
// would remount every row on each parent render, dropping focus from the
// assignee select and swallowing clicks that land mid-refetch.

interface TaskRowProps {
  task: TicketTask;
  myId?: string | null;
  access: TaskAccess;
  canCreate: boolean;
  members: any[];
  linkBase: string;
  busy: boolean;
  onOpen: (task: TicketTask) => void;
  onComplete: (task: TicketTask) => void;
  onReopen: (task: TicketTask) => void;
  onReassign: (task: TicketTask, assignedToId: string | null) => void;
}

function TaskRow({ task, myId, access, canCreate, members, linkBase, busy, onOpen, onComplete, onReopen, onReassign }: TaskRowProps) {
  const finished = task.status === 'completed';
  const manage = canManageTask(task, access);
  const mine = !!myId && task.assignedToId === myId;
  return (
    <li className="px-5 py-2.5 flex items-start gap-3">
      <button
        type="button"
        aria-label={finished ? `Reopen task ${task.title}` : `Complete task ${task.title}`}
        title={!manage ? 'Only staff, collaborators, and the assignee can change this task' : finished ? 'Reopen' : 'Mark complete (a note is required)'}
        disabled={!manage || busy}
        onClick={() => (finished ? onReopen(task) : onComplete(task))}
        className="mt-0.5 shrink-0"
        style={{ background: 'none', border: 'none', padding: 0, cursor: manage ? 'pointer' : 'default', lineHeight: 0 }}
      >
        {finished
          ? <CheckSquare className="w-4 h-4" style={{ color: 'var(--scpa-status-ok)' }} />
          : <Square className="w-4 h-4 text-muted" />}
      </button>
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={() => onOpen(task)}
          className={`text-sm text-left hover:text-primary transition-colors ${finished ? 'line-through text-muted' : 'text-fg font-medium'}`}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          title="Open task: notes, details, actions"
        >
          {task.title}
        </button>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted mt-0.5">
          {finished ? (
            <span>
              Done{task.completedBy ? ` by ${task.completedBy.id === myId ? 'you' : task.completedBy.name}` : ''}
              {task.completedAt ? ` · ${fmtShort(task.completedAt)}` : ''}
            </span>
          ) : task.assignedTo ? (
            <span
              className="font-semibold"
              style={{ color: mine ? 'var(--scpa-primary)' : 'var(--scpa-status-warning)' }}
            >
              {mine ? 'Yours' : `Waiting on ${task.assignedTo.name}`}
            </span>
          ) : (
            <span>Unassigned</span>
          )}
          {task.linkedTicket && (
            <Link
              to={`${linkBase}/${task.linkedTicket.id}`}
              className="inline-flex items-center gap-1 text-primary hover:underline"
              title={task.linkedTicket.title}
            >
              <Link2 className="w-3 h-3" /> {task.linkedTicket.ticketNumber}
              <StatusBadge status={task.linkedTicket.status} />
            </Link>
          )}
        </div>
      </div>
      {manage && !finished && canCreate && (
        <TaskAssigneeSelect
          value={task.assignedToId ?? ''}
          members={members}
          myId={myId}
          disabled={busy}
          onChange={v => onReassign(task, v || null)}
          className="select text-xs py-1 max-w-[11rem]"
        />
      )}
      {manage && !finished && !canCreate && !mine && (
        <button
          type="button"
          className="btn btn-ghost text-xs py-1 px-2"
          onClick={() => onReassign(task, myId ?? null)}
        >
          Take it
        </button>
      )}
    </li>
  );
}

// ─── The card ─────────────────────────────────────────────────

export default function TaskList({
  ticketId, companyId, tasks, summary, myId, isStaff, isCollaborator, linkBase = '/tickets', emailIncludeContent, onChanged,
}: TaskListProps) {
  const qc = useQueryClient();
  const access: TaskAccess = { myId, isStaff, isCollaborator };
  const canCreate = isStaff || isCollaborator;

  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [completing, setCompleting] = useState<TicketTask | null>(null);

  const { data: members = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId!),
    enabled: !!companyId && canCreate,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
    qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
    qc.invalidateQueries({ queryKey: ['ticket-activity', ticketId] });
    qc.invalidateQueries({ queryKey: ['ticket-task'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['tickets'] });
    onChanged?.();
  };

  const createMutation = useMutation({
    mutationFn: () => tasksApi.create(ticketId, { title: newTitle.trim(), assignedToId: newAssignee || null }),
    // Close the row after each add — leaving an empty form under the list
    // read as an unfinished task.
    onSuccess: (t) => { toast.success(`Added "${t.title}"`); setNewTitle(''); setNewAssignee(''); setAdding(false); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message ?? 'Could not add the task'),
  });
  const reassignMutation = useMutation({
    mutationFn: ({ taskId, assignedToId }: { taskId: string; assignedToId: string | null }) =>
      tasksApi.update(ticketId, taskId, { assignedToId }),
    onSuccess: (t) => { toast.success(t.assignedTo ? `Assigned to ${t.assignedTo.id === myId ? 'you' : t.assignedTo.name}` : 'Task unassigned'); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message ?? 'Could not reassign'),
  });
  const reopenMutation = useMutation({
    mutationFn: (taskId: string) => tasksApi.reopen(ticketId, taskId),
    onSuccess: () => { toast.success('Task reopened'); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message ?? 'Could not reopen'),
  });

  const open = tasks.filter(t => t.status !== 'completed');
  const done = tasks.filter(t => t.status === 'completed');
  const s = summary ?? summarizeTasks(tasks);
  const pct = s.total > 0 ? Math.round((s.done / s.total) * 100) : 0;

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    createMutation.mutate();
  };

  const rowProps = {
    myId, access, canCreate, members, linkBase,
    busy: reopenMutation.isPending || reassignMutation.isPending,
    onOpen: (task: TicketTask) => setOpenTaskId(task.id),
    onComplete: (task: TicketTask) => setCompleting(task),
    onReopen: (task: TicketTask) => reopenMutation.mutate(task.id),
    onReassign: (task: TicketTask, assignedToId: string | null) => reassignMutation.mutate({ taskId: task.id, assignedToId }),
  };

  return (
    <div className="card overflow-hidden" style={{ padding: 0 }}>
      <div className="px-5 py-3.5 border-b border-line-light flex items-center justify-between gap-2 flex-wrap">
        <h2 className="section-title text-base flex items-center gap-2 flex-wrap">
          <ListChecks className="w-4 h-4 text-muted" /> Tasks
          {s.total > 0 && (
            <span className="text-xs text-muted font-normal" data-testid="task-summary">{summaryText(s)}</span>
          )}
        </h2>
        {canCreate && (
          <div className="flex items-center gap-1.5">
            <button type="button" className="btn btn-ghost text-xs py-1 px-2" onClick={() => setShowTemplates(true)} title="Add the tasks of a saved template">
              <LayoutTemplate className="w-3.5 h-3.5" /> From template
            </button>
            <button type="button" className="btn btn-secondary text-xs py-1 px-2" onClick={() => setAdding(true)}>
              <Plus className="w-3.5 h-3.5" /> Add task
            </button>
          </div>
        )}
      </div>

      {s.total > 0 && (
        <div className="h-1.5" style={{ background: 'var(--scpa-row-odd)' }} aria-hidden>
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: 'var(--scpa-status-ok)' }} />
        </div>
      )}

      {open.length > 0 && (
        <ul className="divide-y divide-[color:var(--scpa-border-light)]">
          {open.map(task => <TaskRow key={task.id} task={task} {...rowProps} />)}
        </ul>
      )}

      {done.length > 0 && (
        <div className={open.length > 0 ? 'border-t border-line-light' : ''}>
          <button
            type="button"
            className="w-full text-left px-5 py-2 text-xs font-semibold text-muted hover:text-fg inline-flex items-center gap-1"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setShowDone(v => !v)}
          >
            {showDone ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {done.length} completed
          </button>
          {showDone && (
            <ul className="divide-y divide-[color:var(--scpa-border-light)] border-t border-line-light">
              {done.map(task => <TaskRow key={task.id} task={task} {...rowProps} />)}
            </ul>
          )}
        </div>
      )}

      {tasks.length === 0 && !adding && (
        <p className="px-5 py-4 text-xs text-muted">
          No tasks yet.{canCreate ? ' Break the work into tasks here, or start from a template.' : ''}
        </p>
      )}

      {adding && (
        <form onSubmit={submitNew} className="px-5 py-3 border-t border-line-light flex items-center gap-2 flex-wrap">
          <input
            autoFocus
            className="input text-sm flex-1 min-w-[12rem]"
            placeholder="What needs doing?"
            aria-label="Task title"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') { setAdding(false); setNewTitle(''); } }}
          />
          <TaskAssigneeSelect value={newAssignee} members={members} myId={myId} onChange={setNewAssignee} className="select text-sm max-w-[12rem]" />
          <button type="submit" className="btn btn-primary" disabled={!newTitle.trim() || createMutation.isPending}>
            {createMutation.isPending ? 'Adding…' : 'Add'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => { setAdding(false); setNewTitle(''); }}>Cancel</button>
        </form>
      )}

      {completing && (
        <CompleteTaskModal
          ticketId={ticketId}
          task={completing}
          emailIncludeContent={emailIncludeContent}
          onClose={() => setCompleting(null)}
          onDone={invalidate}
        />
      )}
      {showTemplates && (
        <TemplatePickerModal ticketId={ticketId} onClose={() => setShowTemplates(false)} onAdded={invalidate} />
      )}
      {openTaskId && (
        <TaskDetailModal
          ticketId={ticketId}
          taskId={openTaskId}
          linkBase={linkBase}
          members={members}
          myId={myId}
          emailIncludeContent={emailIncludeContent}
          onClose={() => setOpenTaskId(null)}
          onChanged={invalidate}
        />
      )}
    </div>
  );
}
